import { Component, OnInit, Input, ElementRef, AfterViewChecked, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';

import { StateService } from '../../services/state.service';
import { StateRxService } from '../../services/staterx.service';

export enum BannerTypes {
  Information = 'information-banner',
  Survey = 'survey-banner',
}

export enum BannerLocations {
  All = 'all',
  Home = 'home',
  Results = 'results',
  Person = 'person',
  Place = 'place',
}

@Component({
  selector: 'hcf-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  standalone: false,
})
export class BannerComponent implements OnInit, OnDestroy, AfterViewChecked {
  constructor(
    private _router: Router,
    private _stateRxService: StateRxService,
    private _stateService: StateService,
    private _elementRef: ElementRef,
    private _domSanitizer: DomSanitizer,
  ) {}

  @Input() initialY: number = 0;
  bannerType: BannerTypes = BannerTypes.Information;
  bannerLocation: BannerLocations = BannerLocations.All;
  bannerText: SafeHtml = '';
  bannerOrigText: string = '';

  currentPageType: BannerLocations;
  isShown = false;

  customMessageSubscription: Subscription;
  routerSubscription: Subscription;

  ngOnInit() {
    // Retrieve GTM messages
    this.customMessageSubscription = this._stateRxService.customMessage.rxSubscribe((message) => {
      if (message) {
        let displayMessage = '';

        // Set banner body text
        if (message.body) {
          // Truncate notification if too long
          // Allow for a 10 character buffer so that the "split text/body" isn't too short
          const maxLength = 70;
          if (message.body.indexOf('[!]') > -1 || message.body.length > maxLength + 10) {
            // Find next end of word after 50 characters
            if (message.body.indexOf('[!]') > -1) {
              const wordBoundaryIdx = message.body.indexOf('[!]');
              const title = message.body.substring(0, wordBoundaryIdx);
              const body =
                '<div class="notification-body notification-block"><span class="notification-inner-body">' +
                message.body.substring(wordBoundaryIdx + 3) +
                '</span></div>';

              displayMessage =
                '<div class="split-notification"><div class="notification-title">' +
                title +
                '</div>' +
                '<span id="spEllipsis"> <a id="aNotification" href="javascript:void(0);" class="notification-link">read&nbsp;more</a></span>' +
                '<span id="spNotification" style="display:none;">' +
                body +
                '</span></div>';
            } else {
              const wordBoundaryIdx = message.body.substring(maxLength).search(/[^A-Za-z!\.]/) + maxLength; // eslint-disable-line no-useless-escape
              const title = message.body.substring(0, wordBoundaryIdx);
              const body = message.body.substring(wordBoundaryIdx);

              displayMessage =
                '<div class="notification-block">' +
                title +
                '<span id="spEllipsis">... <a id="aNotification" href="javascript:void(0);" class="notification-link">read&nbsp;more</a></span>' +
                '<span id="spNotification" style="display:none;">' +
                body +
                '</span></div>';
            }
          } else {
            displayMessage = message.body;
          }
        } else {
          displayMessage = '';
        }

        // Determine banner type (Informational, Survey)
        if (message.type === BannerTypes.Survey) {
          this.bannerType = BannerTypes.Survey;
        } else {
          this.bannerType = BannerTypes.Information;
        }

        // Determine banner location
        if (message.location) {
          this.bannerLocation = message.location;
        }

        this.bannerOrigText = displayMessage;
        this.bannerText = this.safeHtml(displayMessage);
      } else {
        this.bannerOrigText = '';
        this.bannerText = '';
      }

      // Determine if banner should be shown
      this.isShown =
        this.bannerOrigText &&
        this.bannerOrigText.length > 0 &&
        (this.bannerLocation === BannerLocations.All || this.currentPageType === this.bannerLocation);
    });

    // Retrieve type of current page
    this.routerSubscription = this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this._router.url.startsWith('/search-results')) {
          this.currentPageType = BannerLocations.Results;
        } else if (this._router.url.startsWith('/place-profile')) {
          this.currentPageType = BannerLocations.Place;
        } else if (this._router.url.startsWith('/person-profile')) {
          this.currentPageType = BannerLocations.Person;
        } else {
          this.currentPageType = BannerLocations.Home;
        }
      }

      // Determine if banner should be shown
      this.isShown =
        this.bannerOrigText &&
        this.bannerOrigText.length > 0 &&
        (this.bannerLocation === BannerLocations.All || this.currentPageType === this.bannerLocation);
    });
  }

  // Unsubscribe from message / router updates
  ngOnDestroy() {
    if (this.customMessageSubscription) {
      this.customMessageSubscription.unsubscribe();
    }

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  ngAfterViewChecked() {
    const aNotification = this._elementRef.nativeElement.querySelector('#aNotification');
    const spNotification = this._elementRef.nativeElement.querySelector('#spNotification');
    const spEllipsis = this._elementRef.nativeElement.querySelector('#spEllipsis');
    if (aNotification) {
      aNotification.onclick = () => {
        aNotification.style.display = 'none';
        spEllipsis.style.display = 'none';
        spNotification.style.display = 'inline';
      };
    }
  }

  public closeBanner = () => {
    // Remove GTM message from store
    this._stateService.clearCustomMessage();
  };

  /**
   * Sanitize the text for rendering
   * @param text
   * @returns any
   */
  safeHtml(text: string) {
    return this._domSanitizer.bypassSecurityTrustHtml(text);
  }
}
